import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Space, Tag, Tooltip, Modal, Input, Empty, Dropdown, Alert,
} from 'antd';
import {
  PlusOutlined, ImportOutlined, BellOutlined, FileWordOutlined,
  SearchOutlined, FlagOutlined, MoreOutlined, DeleteOutlined, EditOutlined,
  EyeOutlined, TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import bilateralesService from '../../services/bilateralesService';
import { formatDate, STATUT_MAP } from '../../variables/constants';
import DossierBilateralModal from './components/DossierBilateralModal';
import ImportDossiersModal from './components/ImportDossiersModal';

import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit } from '../../i18n/libelles';

const G = '#1a6b3c';
const INK = '#0f172a';
const MUTED = '#94a3b8';
const BORDER = '#e2e8f0';
const WHITE = '#ffffff';
const SURFACE = '#f8fafc';

const fmt = (v) => (!v && v !== 0) ? '-' : new Intl.NumberFormat(localeIntl()).format(Number(v));

const MARKER_COLORS = avecLibelleTraduit([
  { key: 'ROUGE', bg: '#ef4444' },
  { key: 'ORANGE', bg: '#f97316' },
  { key: 'JAUNE', bg: '#eab308' },
  { key: 'VERT', bg: '#16a34a' },
  { key: 'BLEU', bg: '#3b82f6' },
], 'bilaterales:session.marqueurs', { cle: 'key' });

export default function DetailsSession({ session, rb, onChange }) {
  const { t } = useTranslation('bilaterales');
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [eligibles, setEligibles] = useState([]);
  const [eligiblesLoading, setEligiblesLoading] = useState(false);

  // L'import (fichier Excel ou liste collée) vit dans son propre composant : il porte
  // le parsing du classeur, le choix de colonne et l'aperçu, qui n'ont rien à faire
  // dans l'écran de session.
  const [importOpen, setImportOpen] = useState(false);

  // Participants présents le jour de la séance (figurent sur le PV généré).
  // Stockés en base comme texte multi-lignes ; édités ici sous forme de tags,
  // chaque ajout/retrait est sauvegardé immédiatement.
  const [participants, setParticipants] = useState(session.participants || '');
  const [savingParticipants, setSavingParticipants] = useState(false);
  const [ajoutParticipantVisible, setAjoutParticipantVisible] = useState(false);
  const [nouveauParticipant, setNouveauParticipant] = useState('');
  const listeParticipants = (participants || '').split(/\r?\n/).map((x) => x.trim()).filter(Boolean);

  useEffect(() => { setParticipants(session.participants || ''); }, [session.scTrackingId, session.participants]);

  const sauvegarderParticipants = async (liste) => {
    const texte = liste.join('\n');
    const precedent = participants;
    setParticipants(texte);
    setSavingParticipants(true);
    try {
      await bilateralesService.updateSession(session.scTrackingId, { participants: texte });
      onChange?.();
    } catch (_) {
      toast.error(t('session.participants.erreurEnregistrement'));
      setParticipants(precedent);
    } finally {
      setSavingParticipants(false);
    }
  };

  const ajouterParticipant = () => {
    const v = nouveauParticipant.trim();
    setNouveauParticipant('');
    if (!v) { setAjoutParticipantVisible(false); return; }
    if (!listeParticipants.includes(v)) sauvegarderParticipants([...listeParticipants, v]);
  };

  const retirerParticipant = (p) =>
    sauvegarderParticipants(listeParticipants.filter((x) => x !== p));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bilateralesService.listDossiersOfSession(session.scTrackingId);
      setDossiers(data || []);
    } finally { setLoading(false); }
  }, [session.scTrackingId]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange?.(); };
  const ouvrirDossier = (row) => { setSelected(row); setModalOpen(true); };

  /* ─── Recherche directe et ajout instantané ─── */
  const rechercherEtAjouter = async () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const result = await bilateralesService.chercherSinistreParNumero(searchTerm.trim());
      if (!result) {
        toast.error(t('session.ajout.introuvable'));
        return;
      }
      Modal.confirm({
        title: t('session.ajout.titre', { numero: result.numeroSinistreLocal }),
        content: (
          <div>
            <div><strong>{t('session.ajout.assure')}</strong> {result.assureNomComplet || '-'}</div>
            <div><strong>{t('session.ajout.statut')}</strong> <Tag>{result.statut}</Tag></div>
            {result.dejaDiscuteEnRb && (
              <Alert type="warning" message={t('session.ajout.dejaDiscute', { reference: result.referenceDerniereRb })}
                style={{ marginTop: 8 }} showIcon />
            )}
          </div>
        ),
        okText: t('session.ajout.ajouter'),
        cancelText: t('actions.annuler'),
        onOk: async () => {
          await bilateralesService.addDossierToSession(session.scTrackingId, {
            sinistreTrackingId: result.sinistreTrackingId,
          });
          toast.success(t('session.ajout.succes'));
          setSearchTerm('');
          refresh();
        },
      });
    } catch (_) {
    } finally { setSearching(false); }
  };

  /* ─── Mode DEPLACEMENT : éligibles ─── */
  const ouvrirAjoutEligibles = async () => {
    setAddOpen(true);
    setEligiblesLoading(true);
    try {
      const data = await bilateralesService.sinistresEligibles(
        session.compagnieTrackingId,
        rb.bureauHomologueTrackingId,
      );
      setEligibles(data || []);
    } finally { setEligiblesLoading(false); }
  };

  const ajouterEligibles = async (ids) => {
    try {
      for (const sinistreTrackingId of ids) {
        await bilateralesService.addDossierToSession(session.scTrackingId, { sinistreTrackingId });
      }
      toast.success(t('session.eligibles.ajoutes', { n: ids.length }));
      setAddOpen(false);
      refresh();
    } catch (_) { }
  };

  /* ─── Marqueur (surbrillance) ─── */
  const toggleMarqueur = async (row, couleur) => {
    try {
      if (row.couleurMarqueur === couleur) {
        await bilateralesService.toggleMarqueur(row.dbTrackingId, { marquer: false });
      } else {
        await bilateralesService.toggleMarqueur(row.dbTrackingId, { marquer: true, couleur });
      }
      refresh();
    } catch (_) { }
  };

  /* ─── Supprimer ─── */
  const supprimer = (row) => {
    Modal.confirm({
      title: t('session.retrait.titre'),
      content: t('session.retrait.contenu', { numero: row.numeroSinistreLocal }),
      okText: t('session.retrait.retirer'), okButtonProps: { danger: true }, cancelText: t('actions.annuler'),
      onOk: async () => {
        await bilateralesService.deleteDossier(row.dbTrackingId);
        toast.success(t('session.retrait.succes'));
        refresh();
      },
    });
  };

  /* ─── Colonnes ─── */
  const columns = [
    {
      title: t('session.colonnes.ordre'),
      key: 'ordre',
      width: 48,
      align: 'center',
      render: (_, __, index) => (
        <span style={{ fontWeight: 700, color: MUTED }}>{index + 1}</span>
      ),
    },
    {
      title: '',
      key: 'flag',
      width: 32,
      render: (_, r) => r.marquePourSuivi ? (
        <FlagOutlined style={{
          color: MARKER_COLORS.find((c) => c.key === r.couleurMarqueur)?.bg || '#94a3b8',
          fontSize: 14,
        }} />
      ) : null,
    },
    {
      title: t('session.colonnes.numeroBncbTg'),
      dataIndex: 'numeroSinistreLocal',
      render: (v, r) => (
        <Button type="link" onClick={() => ouvrirDossier(r)}
          style={{ paddingLeft: 0, fontWeight: 600 }}>
          {v}
        </Button>
      ),
    },
    { title: t('session.colonnes.sinBncbHomologue'), dataIndex: 'sinBncbHomologue' },
    { title: t('session.colonnes.sinAssureur'), dataIndex: 'sinAssureurHomologue' },
    { title: t('session.colonnes.victime'), dataIndex: 'victimeNomComplet', render: (v) => v || '-' },
    { title: t('champs.reclame'), dataIndex: 'montantReclame', align: 'right', render: fmt },
    { title: t('champs.bap'), dataIndex: 'montantBap', align: 'right', render: fmt },
    {
      title: t('session.colonnes.fgTg'), dataIndex: 'fgTg', align: 'right',
      render: (v) => <span style={{ color: MUTED }}>{fmt(v)}</span>
    },
    {
      title: t('session.colonnes.bapTotal'), dataIndex: 'bapTotal', align: 'right',
      render: (v) => <strong>{fmt(v)}</strong>
    },
    {
      title: t('champs.statut'),
      dataIndex: 'statutSinistreActuel',
      render: (v) => v ? <Tag color={STATUT_MAP?.[v]?.color}>{v}</Tag> : '-',
    },
    {
      title: t('session.colonnes.datePromise'),
      dataIndex: 'datePromesseAssureur',
      render: (v, r) => v ? (
        <Tooltip title={t('session.rappel', { date: formatDate(r.dateRappel) })}>
          <Tag icon={<BellOutlined />} color="gold">{formatDate(v)}</Tag>
        </Tooltip>
      ) : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, r) => (
        <Space size={0}>
          <Tooltip title={t('session.decisionSeance')}>
            <Button size="small" icon={<EditOutlined />} type="text" onClick={() => ouvrirDossier(r)} />
          </Tooltip>
          <Tooltip title={t('session.ouvrirSinistre')}>
            <Button size="small" icon={<EyeOutlined />} type="text"
              onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)} />
          </Tooltip>
          <Dropdown
            menu={{
              items: [
                ...MARKER_COLORS.map((c) => ({
                  key: c.key,
                  label: (
                    <Space>
                      <span style={{
                        display: 'inline-block', width: 12, height: 12,
                        borderRadius: 3, background: c.bg
                      }} />
                      {c.label}
                      {r.couleurMarqueur === c.key && ' ✓'}
                    </Space>
                  ),
                  onClick: () => toggleMarqueur(r, c.key),
                })),
                { type: 'divider' },
                {
                  key: 'delete',
                  danger: true,
                  icon: <DeleteOutlined />,
                  label: t('session.retrait.retirer'),
                  onClick: () => supprimer(r),
                },
              ],
            }}
          >
            <Button size="small" icon={<MoreOutlined />} type="text" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const totalBap = dossiers.reduce((acc, d) => acc + (Number(d.bapTotal) || 0), 0);
  const totalReclame = dossiers.reduce((acc, d) => acc + (Number(d.montantReclame) || 0), 0);
  const totalFg = dossiers.reduce((acc, d) => acc + (Number(d.fgTg) || 0), 0);
  const nbMarques = dossiers.filter((d) => d.marquePourSuivi).length;
  const isReception = rb.direction === 'RECEPTION';

  const exportSessionWord = () =>
    bilateralesService.downloadExport(
      bilateralesService.exportSessionWordUrl(session.scTrackingId),
      `PV-${rb.reference}-${session.compagnieCode}-${session.dateSession}.docx`,
    );

  return (
    <div className="bilat">

      {/* Ligne de résumé unique : totaux de la séance + participants discrets */}
      <div style={{
        background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10,
        padding: '10px 14px', marginBottom: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', rowGap: 8 }}>
          {[
            { label: t('champs.dossiers'), value: dossiers.length },
            { label: t('champs.reclame'), value: totalReclame, money: true },
            { label: t('champs.bap'), value: totalBap - totalFg, money: true },
            { label: t('champs.fgTg'), value: totalFg, money: true, muted: true },
            { label: t('champs.bapTotal'), value: totalBap, money: true, highlight: true },
          ].map((m, i) => (
            <MetricInline key={m.label} {...m} first={i === 0} />
          ))}
        </div>

        {/* Participants : badges compacts, sauvegarde immédiate, repris sur le PV */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          marginTop: 10, paddingTop: 9, borderTop: `1px solid ${BORDER}`
        }}>
          <Tooltip title={t('session.participants.aide')}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11, fontWeight: 600, color: MUTED, letterSpacing: 0.6,
              textTransform: 'uppercase', marginRight: 4
            }}>
              <TeamOutlined /> {t('session.participants.titre')}
            </span>
          </Tooltip>
          {listeParticipants.map((p) => (
            <Tag
              key={p}
              closable={!savingParticipants}
              onClose={(e) => { e.preventDefault(); retirerParticipant(p); }}
              style={{ borderRadius: 14, padding: '1px 9px', margin: 0, fontSize: 12 }}
            >
              {p}
            </Tag>
          ))}
          {ajoutParticipantVisible ? (
            <Input
              size="small"
              autoFocus
              value={nouveauParticipant}
              onChange={(e) => setNouveauParticipant(e.target.value)}
              onPressEnter={ajouterParticipant}
              onBlur={ajouterParticipant}
              placeholder={t('session.participants.placeholder')}
              style={{ width: 220 }}
            />
          ) : (
            <Tag
              onClick={() => setAjoutParticipantVisible(true)}
              style={{
                borderStyle: 'dashed', cursor: 'pointer', background: 'transparent',
                borderRadius: 14, padding: '1px 9px', margin: 0, fontSize: 12, color: '#64748b'
              }}
            >
              <PlusOutlined /> {listeParticipants.length ? t('session.ajout.ajouter') : t('session.participants.ajouter')}
            </Tag>
          )}
        </div>
      </div>

      {/* Tableau des sinistres : recherche, ajout et actions sur une seule ligne fine. */}
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          padding: '8px 12px', borderBottom: `1px solid ${BORDER}`, background: SURFACE
        }}>
          <Input.Search
            placeholder={t('session.recherche.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={rechercherEtAjouter}
            enterButton={<><SearchOutlined /> {t('session.ajout.ajouter')}</>}
            loading={searching}
            size="small"
            style={{ maxWidth: 400, flex: '1 1 240px' }}
          />
          <div style={{ flex: 1 }} />
          {nbMarques > 0 && (
            <Tooltip title={t('session.marques.aide')}>
              <Tag icon={<FlagOutlined />} style={{ borderRadius: 12, margin: 0 }}>
                {t('session.marques.nombre', { count: nbMarques })}
              </Tag>
            </Tooltip>
          )}
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                isReception
                  ? { key: 'import', icon: <ImportOutlined />, label: t('session.menu.importer'), onClick: () => setImportOpen(true) }
                  : { key: 'eligibles', icon: <PlusOutlined />, label: t('session.menu.eligibles'), onClick: ouvrirAjoutEligibles },
                { type: 'divider' },
                { key: 'pv', icon: <FileWordOutlined />, label: t('session.menu.pv'), onClick: exportSessionWord },
              ],
            }}
          >
            <Button size="small" icon={<MoreOutlined />}>{t('rencontre.actions')}</Button>
          </Dropdown>
        </div>

        {dossiers.length === 0 ? (
          <div style={{ padding: '32px 16px' }}>
            <Empty
              description={
                <div>
                  {t('session.vide.titre')}
                  <div style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>
                    {isReception
                      ? t('session.vide.aideReception')
                      : t('session.vide.aideDeplacement')}
                  </div>
                </div>
              }
            />
          </div>
        ) : (
          <Table
            rowKey="dbTrackingId"
            loading={loading}
            dataSource={dossiers}
            columns={columns}
            size="middle"
            pagination={false}
            tableLayout="auto"
            rowClassName={(r) => r.marquePourSuivi && r.couleurMarqueur
              ? `marker-${r.couleurMarqueur}` : ''}
          />
        )}
      </div>

      <DossierBilateralModal
        open={modalOpen}
        dossier={selected}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); refresh(); }}
      />

      <ModalAjoutEligibles
        open={addOpen}
        loading={eligiblesLoading}
        eligibles={eligibles}
        onCancel={() => setAddOpen(false)}
        onSubmit={ajouterEligibles}
      />

      <ImportDossiersModal
        open={importOpen}
        sessionTrackingId={session.scTrackingId}
        onClose={() => setImportOpen(false)}
        onImported={refresh}
      />
    </div>
  );
}

/* ─── Métrique compacte de l'en-tête compagnie. Zéro → estompé (« - » pour
   les montants) afin de ne pas alourdir la ligne avant saisie. ─── */
function MetricInline({ label, value, money = false, muted = false, highlight = false, first = false }) {
  const vide = !Number(value);
  const affiche = vide ? (money ? '-' : '0') : fmt(value) + (money ? ' FCFA' : '');
  return (
    <div style={{
      padding: first ? '0 14px 0 0' : '0 14px',
      borderLeft: first ? 'none' : `1px solid ${BORDER}`
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: MUTED,
        textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap'
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'var(--font-main)', fontSize: 13.5, fontWeight: 700,
        whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
        color: vide ? '#cbd5e1' : highlight ? G : muted ? MUTED : INK,
      }}>
        {affiche}
      </div>
    </div>
  );
}

/* ─── Modal éligibles ─── */
function ModalAjoutEligibles({ open, loading, eligibles, onCancel, onSubmit }) {
  const { t } = useTranslation('bilaterales');
  const [selectedIds, setSelectedIds] = useState([]);
  useEffect(() => { if (open) setSelectedIds([]); }, [open]);

  const cols = [
    { title: t('session.eligibles.numeroSinistre'), dataIndex: 'numeroSinistreLocal' },
    { title: t('session.eligibles.dateAccident'), dataIndex: 'dateAccident', render: formatDate },
    { title: t('session.eligibles.assure'), dataIndex: 'assureNomComplet' },
    { title: t('champs.statut'), dataIndex: 'statut', render: (v) => <Tag>{v}</Tag> },
    {
      title: t('session.eligibles.dejaDiscute'),
      dataIndex: 'dejaDiscuteEnRb',
      render: (v, r) => v ? <Tag color="orange">{r.referenceDerniereRb}</Tag> : '-',
    },
  ];

  return (
    <Modal
      open={open}
      title={t('session.eligibles.titre')}
      onCancel={onCancel}
      onOk={() => onSubmit(selectedIds)}
      okText={t('session.eligibles.ajouter', { n: selectedIds.length })}
      okButtonProps={{ disabled: selectedIds.length === 0 }}
      cancelText={t('actions.annuler')}
      width={900}
      destroyOnClose
    >
      <p style={{ color: MUTED, marginTop: 0 }}>
        {t('session.eligibles.filtrage')}
      </p>
      <Table
        rowKey="sinistreTrackingId"
        loading={loading}
        dataSource={eligibles}
        columns={cols}
        size="small"
        pagination={{ pageSize: 50 }}
        rowSelection={{ selectedRowKeys: selectedIds, onChange: setSelectedIds }}
        scroll={{ y: 380 }}
      />
    </Modal>
  );
}
